/**
 * Search-param validators for the three standalone report routes.
 *
 * Same rationale as `payroll/-lib/search.ts`: zod is not a dependency of
 * `apps/web`, so these are hand-written coercers with `.catch()` semantics —
 * bad input falls back to the documented default rather than throwing.
 */
import {
  asBool,
  asEnum,
  asId,
  asInt,
  asString,
} from '../../payroll/-lib/search';

/* ─────────────────────── /reports/reconciliation (T5) ───────────────────── */

export interface ReconciliationSearch {
  runId?: string;
  baseline?: string;
  onlyVariances: boolean;
  q?: string;
  page: number;
}

export function validateReconciliationSearch(
  search: Record<string, unknown>,
): ReconciliationSearch {
  const runId = asId(search['runId']);
  const baseline = asId(search['baseline']);
  const q = asString(search['q']);
  return {
    ...(runId ? { runId } : {}),
    ...(baseline ? { baseline } : {}),
    ...(q ? { q } : {}),
    onlyVariances: asBool(search['onlyVariances'], true),
    page: asInt(search['page'], 1),
  };
}

/* ───────────────────────── /reports/variance (T7) ───────────────────────── */

export interface VarianceSearch {
  runId?: string;
  compareToRunId?: string;
  /** `Factor` on the wire — 6 dp. Default 15.000000, the prototype's ±15 %. */
  thresholdPercent: string;
  onlyOutliers: boolean;
  page: number;
}

export function validateVarianceSearch(search: Record<string, unknown>): VarianceSearch {
  const runId = asId(search['runId']);
  const compareToRunId = asId(search['compareToRunId']);
  const threshold = asString(search['thresholdPercent']);
  return {
    ...(runId ? { runId } : {}),
    ...(compareToRunId ? { compareToRunId } : {}),
    thresholdPercent:
      threshold && /^\d{1,3}(\.\d{1,6})?$/.test(threshold) ? threshold : '15.000000',
    onlyOutliers: asBool(search['onlyOutliers'], true),
    page: asInt(search['page'], 1),
  };
}

/* ───────────────────────── /reports/register (T8) ───────────────────────── */

export const REGISTER_GROUPINGS = [
  'NONE',
  'TEAM',
  'AGENT',
  // No 'PAYING_ENTITY' — see `REGISTER_GROUP_BY`; an old link carrying it falls back to NONE.
  'NATIONALITY',
] as const;
export type RegisterGrouping = (typeof REGISTER_GROUPINGS)[number];

export interface RegisterSearch {
  runId?: string;
  groupBy: RegisterGrouping;
  /** Comma-separated team ids; absent is every team (the job reports' `teams`). */
  teams?: string;
  page: number;
}

export function validateRegisterSearch(search: Record<string, unknown>): RegisterSearch {
  const runId = asId(search['runId']);
  return {
    ...(runId ? { runId } : {}),
    groupBy: asEnum(search['groupBy'], REGISTER_GROUPINGS, 'NONE'),
    ...(typeof search['teams'] === 'string' && search['teams'].trim() !== ''
      ? { teams: search['teams'].trim() }
      : {}),
    page: asInt(search['page'], 1),
  };
}

/* ─────────────────────── /reports/daily-earnings (T12) ───────────────────────
 *
 * The grid takes the same filters as the register and reuses its groupings
 * verbatim — the client's SUMMARY-CFSB sheet is the two reports side by side and
 * sections them identically, so a second enum would be a way for them to drift.
 */
export type DailyEarningsSearch = RegisterSearch;

export const validateDailyEarningsSearch = validateRegisterSearch;
